import moment, { duration } from "moment";
import React from "react";
import { useEffect } from "react";
import { useState } from 'react';
import entities from "../utils/entities";
import { useLocalStorage } from "../utils/Hooks/useLocalStorage";
import { useSignalR } from './SignalRContext';
import useFetch from "./useFetch";
type error = boolean;
type loading = boolean;
const useFetchCached = <T>(url: string, options?: { method?: string, forceRefresh?: boolean }): [T, loading, error, React.Dispatch<void>] => {
    const [cache, setCache] = useLocalStorage<{ data: T, cachedAt: Date }>(url);
    const [data, loading, error, refresh] = useFetch<T>(url, options?.method ? { ...options, method: options.method } : undefined, options?.forceRefresh ? undefined : false);
    useEffect(() => {
        // If data is either a defined primative, or a non-empty object
        if (data) {
            setCache({ data, cachedAt: new Date() });
        }
    }, [data, url, setCache]);
    useEffect(() => {
        // Build the cache if it's empty, refresh it if it's too old
        if (typeof cache === "undefined" || duration(moment().diff(moment(cache.cachedAt))).asHours() > 6)
            refresh();
    }, [cache, refresh]);
    return [cache?.data as T, loading, error, refresh];
}

const useFetchEntity = <T extends typeof entities[keyof typeof entities] & { type?: unknown, getAll?: () => string, getByStatus?: (...args: unknown[]) => string, name: string }>(entity: T, params?: unknown) => {
    const [data, loading, error, refresh] = useFetch<Exclude<T['type'], null>>((entity.getAll ?? entity.getByStatus)?.(params as string) ?? "");
    const { hubMessage } = useSignalR();
    const [lastHub, setHubMessage] = useState(undefined as undefined | string);

    useEffect(() => {
        if (entity && hubMessage && lastHub !== hubMessage.id)
            if (hubMessage && hubMessage.entityType === entity.name) {
                refresh instanceof Function && refresh();
                setHubMessage(hubMessage.id);
            }
    }, [hubMessage, entity, refresh, lastHub]);

    return [data, loading, error, refresh] as [typeof data, typeof loading, typeof error, typeof refresh];
}
const useFetchEntitySingle = (entity, itemId, auto = false) => {
    const [data, loading, error, refresh, fetchData] = useFetch(entity && itemId ? entity.getSingle + itemId : null, undefined, auto);
    const sr = useSignalR();

    const [lastHub, setHubMessage] = useState(undefined as undefined | string);
    const hubMessage = sr.hubMessage;
    React.useEffect(() => {
        if (hubMessage && entity && hubMessage.entityType === entity.name) {
            if (hubMessage && lastHub !== hubMessage.id && itemId && hubMessage.itemId === itemId) {
                fetchData instanceof Function && fetchData(true);
                setHubMessage(hubMessage.id);
            }
        }
    }, [hubMessage, entity, itemId, refresh, lastHub, fetchData]);
    return [data, loading, error, refresh, fetchData, itemId];
}
export { useFetchEntitySingle, useFetchEntity, useFetchCached };